import React, { useEffect, useRef, useState } from "react";
import { Map as MapLibreMap, Marker, LngLatBounds, addProtocol, setWorkerUrl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol, PMTiles } from "pmtiles";
import { layers } from "@protomaps/basemaps";
import polyline from "@mapbox/polyline";
import { useConfig } from "../../../../context/ConfigContext";
import {
  DirectionsInfos,
  DirectionsInfosDistance,
  DirectionsInfosTime,
} from "./styled";
import marker from "./marker.png";
import markerDestination from "./marker-destination.png";
import spriteJson from "./assets/sprites/light.json";
import spriteJson2x from "./assets/sprites/light@2x.json";
import spritePng from "./assets/sprites/light.png";
import spritePng2x from "./assets/sprites/light@2x.png";
import notoRegular0 from "./assets/fonts/Noto_Sans_Regular/0-255.pbf";
import notoRegular1 from "./assets/fonts/Noto_Sans_Regular/256-511.pbf";
import notoMedium0 from "./assets/fonts/Noto_Sans_Medium/0-255.pbf";
import notoMedium1 from "./assets/fonts/Noto_Sans_Medium/256-511.pbf";
import notoItalic0 from "./assets/fonts/Noto_Sans_Italic/0-255.pbf";
import notoItalic1 from "./assets/fonts/Noto_Sans_Italic/256-511.pbf";
// the palette (Google Maps-like); the admin maps read the same file
import flavor from "./map-flavor.json";

// The map works with no network: the basemap is one PMTiles archive per
// tenant (config.map_tiles, cached whole on a box by src/sync.js and served by
// byte range from the service worker), the route to each atração comes
// precomputed in the API payload, and the style's fonts + sprites are bundled
// here (so they are precached with the app) and handed to MapLibre through
// the folio:// protocol below. Only Latin glyph ranges ship; a label needing
// another range renders without those characters instead of failing.
const BUNDLED = {
  "sprites/light.json": spriteJson,
  "sprites/light@2x.json": spriteJson2x,
  "sprites/light.png": spritePng,
  "sprites/light@2x.png": spritePng2x,
  "fonts/Noto Sans Regular/0-255.pbf": notoRegular0,
  "fonts/Noto Sans Regular/256-511.pbf": notoRegular1,
  "fonts/Noto Sans Medium/0-255.pbf": notoMedium0,
  "fonts/Noto Sans Medium/256-511.pbf": notoMedium1,
  "fonts/Noto Sans Italic/0-255.pbf": notoItalic0,
  "fonts/Noto Sans Italic/256-511.pbf": notoItalic1,
};

// MapLibre's worker is an ES module it locates through import.meta.url, which
// this bundle resolves to the page URL (the SPA answers that with index.html).
// The two module files are copied to public/maplibre/ by the build (package.json
// "maplibre-worker") and precached by the service worker, so they load offline.
setWorkerUrl(process.env.PUBLIC_URL + "/maplibre/maplibre-gl-worker.mjs");
addProtocol("pmtiles", new Protocol().tile);
addProtocol("folio", async (params) => {
  const key = decodeURIComponent(params.url.replace(/^folio:\/\//, ""));
  const asset = BUNDLED[key];
  if (params.type === "json") {
    if (asset === undefined) throw new Error("map asset not bundled: " + key);
    return { data: asset };
  }
  // an unbundled glyph range: no glyphs rather than an error per label
  if (asset === undefined) return { data: new ArrayBuffer(0) };
  const res = await fetch(asset);
  return { data: await res.arrayBuffer() };
});

const buildStyle = (tilesUrl) => ({
  version: 8,
  glyphs: "folio://fonts/{fontstack}/{range}.pbf",
  sprite: "folio://sprites/light",
  sources: {
    protomaps: {
      type: "vector",
      url: "pmtiles://" + tilesUrl,
      attribution: "© OpenStreetMap",
    },
  },
  layers: layers("protomaps", flavor, { lang: "pt" }),
});

const EMPTY = { type: "FeatureCollection", features: [] };
const CLOSE_ZOOM = 16;
// The map is always rendered at the open panel's size (EmpreendimentosMapEmbed
// ".in" in ../../styled) and centred in its wrapper, which clips it: closed, the
// round thumbnail shows the middle of the same canvas. Animating the wrapper
// instead of the map means no resize, no relayout and no bare wrapper
// background showing while the panel grows.
const MAP_WIDTH = 960;
const MAP_HEIGHT = 1080;

const makeMarker = (src, width, height) => {
  const el = document.createElement("img");
  el.src = src;
  el.style.width = width + "px";
  el.style.height = height + "px";
  el.style.display = "block";
  return el;
};

const MapsInner = ({ mapStyle, center, route, tilesUrl }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const originMarkerRef = useRef(null);
  const destMarkerRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const map = new MapLibreMap({
      container: containerRef.current,
      style: buildStyle(tilesUrl),
      center: [center.lng, center.lat],
      zoom: CLOSE_ZOOM,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    });
    map.touchZoomRotate.disableRotation();
    // The archive only holds every zoom inside the tenant's region; outside
    // it the middle zooms are empty. Confine the camera to the region (its
    // bounds come from the archive header) and stop zooming out once the
    // region fills the view, so no blank area ever shows.
    new PMTiles(tilesUrl)
      .getHeader()
      .then((h) => {
        const bounds = [
          [h.minLon, h.minLat],
          [h.maxLon, h.maxLat],
        ];
        const fit = map.cameraForBounds(bounds);
        if (fit) map.setMinZoom(fit.zoom);
        map.setMaxBounds(bounds);
      })
      .catch((e) => console.log("Maps bounds: ", e && e.message));
    map.on("load", () => {
      map.addSource("route", { type: "geojson", data: EMPTY });
      map.addLayer({
        id: "route-line",
        type: "line",
        source: "route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#141414", "line-width": 4 },
      });
      setReady(true);
    });
    map.on("error", (e) => {
      console.log("Maps error: ", e && e.error ? e.error.message : e);
    });
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // the map is created once; center/tiles changes are applied below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // origin marker + view, when no route is selected
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (!originMarkerRef.current) {
      originMarkerRef.current = new Marker({
        element: makeMarker(marker, 32 / 1.5, 42 / 1.5),
        anchor: "bottom",
      })
        .setLngLat([center.lng, center.lat])
        .addTo(map);
    } else {
      originMarkerRef.current.setLngLat([center.lng, center.lat]);
    }
    if (!route) {
      map.easeTo({ center: [center.lng, center.lat], zoom: CLOSE_ZOOM, duration: 800 });
    }
  }, [ready, center, route]);

  // the selected atração: its stored route, both markers, fit the view
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const source = map.getSource("route");
    if (destMarkerRef.current) {
      destMarkerRef.current.remove();
      destMarkerRef.current = null;
    }
    if (!route || !route.dest) {
      if (source) source.setData(EMPTY);
      return;
    }
    destMarkerRef.current = new Marker({
      element: makeMarker(markerDestination, 60 / 1.2, 41 / 1.2),
      anchor: "bottom",
    })
      .setLngLat([route.dest.lng, route.dest.lat])
      .addTo(map);

    const bounds = new LngLatBounds();
    bounds.extend([center.lng, center.lat]);
    bounds.extend([route.dest.lng, route.dest.lat]);
    if (route.polyline) {
      const coords = polyline.decode(route.polyline).map(([lat, lng]) => [lng, lat]);
      coords.forEach((c) => bounds.extend(c));
      if (source) {
        source.setData({
          type: "FeatureCollection",
          features: [{ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: coords } }],
        });
      }
    } else if (source) {
      source.setData(EMPTY);
    }
    map.fitBounds(bounds, { padding: 90, duration: 800, maxZoom: CLOSE_ZOOM });
  }, [ready, center, route]);

  const hasInfos = !!(route && route.polyline);
  const distance = hasInfos ? (route.distance_m / 1000).toFixed(2) + "km" : "0m";
  const time = hasInfos ? Math.round(route.duration_s / 60) + "min" : "0min";

  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: "absolute",
          width: MAP_WIDTH + "px",
          height: MAP_HEIGHT + "px",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: mapStyle.pointerEvents,
        }}
      />
      <DirectionsInfos className={hasInfos ? "in" : ""}>
        <DirectionsInfosDistance>{distance}</DirectionsInfosDistance>
        <DirectionsInfosTime>{time}</DirectionsInfosTime>
      </DirectionsInfos>
    </>
  );
};

const Note = ({ children }) => (
  <div
    style={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      opacity: 0.7,
      padding: "2em",
    }}
  >
    {children}
  </div>
);

// No tiles built for the tenant yet, or an empreendimento whose address did
// not geocode: a note instead of a blank map. Both are admin-side fixes
// (folio:map-tiles / the coordinates on the empreendimento form).
const Maps = ({ mapStyle, center, route }) => {
  const { appConfig } = useConfig();
  const tilesUrl = appConfig.mapTiles;
  if (!tilesUrl) return <Note>Mapa não configurado</Note>;
  if (!center) return <Note>Endereço sem localização no mapa</Note>;
  return <MapsInner mapStyle={mapStyle} center={center} route={route} tilesUrl={tilesUrl} />;
};

export default React.memo(Maps);
